import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getSetting } from '../../lib/settings'
import { CLOSED_DEAL_STAGES } from '../../lib/stages'
import { ACTIVITY_CHANGED, CONTACTS_CHANGED, DEALS_CHANGED } from '../shared/events'

/**
 * Data for the Today screen's focus island: tasks due today or overdue,
 * open deals closing in the next two weeks, and contacts going cold.
 *
 * All queries are per-user (`user_id = auth.uid()` under RLS); the explicit
 * `.eq('user_id', …)` mirrors the legacy loaders.
 */

export interface TodayTask {
  id: string
  title: string
  due_date: string | null
  contact_id: string | null
  contacts: {
    full_name: string | null
    first_name: string | null
    company_name: string | null
  } | null
}

export interface TodayDeal {
  id: string
  title: string
  stage: string
  value: number | null
  close_date: string | null
  accounts: { company_name: string | null } | null
}

export interface ColdContact {
  contact_id: string
  lifecycle_stage: string | null
  last_contacted: string | null
  contacts: {
    full_name: string | null
    first_name: string | null
    last_name: string | null
    company_name: string | null
  } | null
}

export interface TodayData {
  status: 'loading' | 'ready' | 'error'
  tasks: TodayTask[]
  deals: TodayDeal[]
  cold: ColdContact[]
  contactCount: number
  dealCount: number
  rotDays: number
}

/** yyyy-mm-dd in the browser's own timezone (what "today" means to the rep). */
export function localDay(offsetDays = 0, from = new Date()): string {
  const d = new Date(from)
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

// Contacts in these lifecycle stages are finished; they never "go cold".
// Same rule the People table uses for its stale marker.
const FINISHED_STAGES = new Set(['closed', 'do not contact'])

const DEAL_WINDOW_DAYS = 14
const LIST_LIMIT = 5

export function useTodayData(userId: string | null) {
  const [data, setData] = useState<TodayData>({
    status: 'loading',
    tasks: [],
    deals: [],
    cold: [],
    contactCount: 0,
    dealCount: 0,
    rotDays: 30,
  })

  const load = useCallback(async () => {
    if (!userId) return
    const today = localDay()
    const rotDays = Number(getSetting('rot_days', '30')) || 30
    const cutoff = new Date(Date.now() - rotDays * 86400000).toISOString()
    const closed = `(${CLOSED_DEAL_STAGES.map((s) => `"${s}"`).join(',')})`
    try {
      const [tasksR, dealsR, coldR, contactCountR, dealCountR] = await Promise.all([
        supabase
          .from('tasks')
          .select('id,title,due_date,contact_id,contacts(full_name,first_name,company_name)')
          .eq('user_id', userId)
          .eq('done', false)
          .lte('due_date', today)
          .order('due_date', { ascending: true })
          .limit(8),
        supabase
          .from('deals')
          .select('id,title,stage,value,close_date,accounts(company_name)')
          .eq('user_id', userId)
          .not('stage', 'in', closed)
          .not('close_date', 'is', null)
          .lte('close_date', localDay(DEAL_WINDOW_DAYS))
          .order('close_date', { ascending: true })
          .limit(LIST_LIMIT),
        supabase
          .from('user_contacts')
          .select(
            'contact_id,lifecycle_stage,last_contacted,contacts(full_name,first_name,last_name,company_name)',
          )
          .eq('user_id', userId)
          .or(`last_contacted.is.null,last_contacted.lt.${cutoff}`)
          .order('priority_score', { ascending: false, nullsFirst: false })
          .limit(20),
        supabase
          .from('user_contacts')
          .select('contact_id', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase.from('deals').select('id', { count: 'exact', head: true }).eq('user_id', userId),
      ])
      const err = tasksR.error || dealsR.error || coldR.error
      if (err) throw err
      const cold = ((coldR.data ?? []) as unknown as ColdContact[])
        .filter((r) => r.contacts && !FINISHED_STAGES.has((r.lifecycle_stage ?? '').toLowerCase()))
        .slice(0, LIST_LIMIT)
      setData({
        status: 'ready',
        tasks: (tasksR.data ?? []) as unknown as TodayTask[],
        deals: (dealsR.data ?? []) as unknown as TodayDeal[],
        cold,
        contactCount: contactCountR.count ?? 0,
        dealCount: dealCountR.count ?? 0,
        rotDays,
      })
    } catch {
      setData((d) => ({ ...d, status: 'error' }))
    }
  }, [userId])

  useEffect(() => {
    void load()
  }, [load])

  // Stay current when another island (People, Deals) changes data.
  useEffect(() => {
    const bus = window.ShaunIslands?.events
    if (!bus) return
    const handler = () => void load()
    for (const ev of [CONTACTS_CHANGED, DEALS_CHANGED, ACTIVITY_CHANGED])
      bus.addEventListener(ev, handler)
    return () => {
      for (const ev of [CONTACTS_CHANGED, DEALS_CHANGED, ACTIVITY_CHANGED])
        bus.removeEventListener(ev, handler)
    }
  }, [load])

  /** Optimistically drop a task from the list (after it is marked done). */
  const removeTask = useCallback((id: string) => {
    setData((d) => ({ ...d, tasks: d.tasks.filter((t) => t.id !== id) }))
  }, [])

  return { data, reload: load, removeTask }
}
